import { useAuth } from '@/hooks/use-auth';
import { getAppVersion } from '@/lib/api/client';
import { useCallback, useEffect, useState } from 'react';
import { fetchReleaseNotes } from './api';
import { compareSemver } from './compareSemver';
const SEEN_PREFIX = 'nl.whatsNew.seen:';
/**
 * Per-account key for the last release the user has acknowledged. Keyed by email
 * so multiple accounts on one browser don't share state. Device-local (localStorage);
 * promoting it to server-side preferences would make the one-time view follow the user
 * across devices — a deliberate later upgrade.
 */
function seenKey(email) {
    return `${SEEN_PREFIX}${email ?? 'anon'}`;
}
function readSeen(email) {
    try {
        return localStorage.getItem(seenKey(email));
    }
    catch {
        return null;
    }
}
/**
 * Release-notes state for the current user: the (version-eligible) history, the current release,
 * whether it's still unacknowledged, and a way to mark it acknowledged.
 *
 * Notes are fetched from the ControlPlane (global store) and version-gated to the SPA's running
 * build, so a note never surfaces before its release actually reaches the user — preserving the
 * old "ships with the bundle" guarantee now that the source is the DB. The returned shape is
 * unchanged, so the provider / modal / history page need no changes.
 */
export function useReleaseNotes() {
    const { userEmail, isAuthenticated } = useAuth();
    const [notes, setNotes] = useState([]);
    // Bump to force a re-read of the seen marker after markSeen.
    const [, bump] = useState(0);
    // Fetch once authenticated (the endpoint is authed). Notes are non-critical chrome, so a failure
    // just yields no "What's new" — it never surfaces an error to the user. We don't reset state
    // synchronously when unauthenticated (that would be a setState-in-effect cascade); the derived
    // `visible` below gates on auth instead.
    useEffect(() => {
        if (!isAuthenticated)
            return;
        let cancelled = false;
        fetchReleaseNotes()
            .then((fetched) => {
            if (!cancelled)
                setNotes(fetched);
        })
            .catch(() => {
            if (!cancelled)
                setNotes([]);
        });
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);
    // Hide everything while logged out (any stale fetched notes are ignored until re-auth).
    const visible = isAuthenticated ? notes : [];
    // Version gate: only notes at or below the SPA's running build version. Unknown running version
    // (local dev / tests) → no gate, show all.
    const running = getAppVersion();
    const eligible = running ? visible.filter((n) => compareSemver(n.version, running) <= 0) : visible;
    const latest = eligible.length > 0
        ? eligible.reduce((a, b) => (compareSemver(b.version, a.version) > 0 ? b : a))
        : undefined;
    const seenVersion = readSeen(userEmail);
    const markSeen = useCallback(() => {
        if (!latest)
            return;
        try {
            localStorage.setItem(seenKey(userEmail), latest.version);
        }
        catch {
            /* ignore unavailable storage */
        }
        bump((n) => n + 1);
    }, [latest, userEmail]);
    return {
        all: eligible,
        latest,
        // Suppress the auto-popup for a release with no user-facing changes (empty highlights).
        hasUnseen: !!latest && seenVersion !== latest.version && latest.highlights.length > 0,
        markSeen,
    };
}
